import { type ReactElement } from 'react'
import { isEmpty, pluralize, resolveReportColumnValues, type ReportColumnDefinition } from '@terros/common'
import { useReportColumnsMetadata, useSafeCurrentWorkflow, useTranslation } from '@front/model'
import {
  ToggleColumns,
  type TableColumnDef,
  type ToggleTableColumnsProps,
  useReportColumnVariantToggle,
} from '@components'

type Props = Omit<ToggleTableColumnsProps<TableColumnDef>, 'selectedColumns' | 'setSelectedColumns'> & {
  selectedColumns: string[]
  setSelectedColumns: (cols: string[]) => void
}

export const ToggleStatsColumns = ({
  selectedColumns,
  setSelectedColumns,
  label,
  title,
  ...props
}: Props): ReactElement => {
  const { t } = useTranslation()
  const workflowId = useSafeCurrentWorkflow()?.workflow.id
  const { columns: reportColumns, variantMap } = useReportColumnsMetadata(workflowId)
  const renderColumnToggle = useReportColumnVariantToggle(variantMap)

  const allColumns = reportColumns.map((column) => toTableColumnDef(column))

  const toggleableSelectedColumns = isEmpty(reportColumns)
    ? selectedColumns.filter((col) => col !== 'groupBy')
    : resolveReportColumnValues(selectedColumns, reportColumns).filter((col) => col !== 'groupBy')
  const resolvedLabel =
    label ??
    t('{{count}} {{pluralizedMetric}} selected', {
      count: toggleableSelectedColumns.length,
      pluralizedMetric: pluralize(toggleableSelectedColumns, t('metric'), t('metrics')),
    })
  const resolvedTitle = title ?? t('Metrics')

  return (
    <ToggleColumns
      {...props}
      columns={allColumns}
      renderColumnToggle={renderColumnToggle}
      selectedColumns={toggleableSelectedColumns}
      setSelectedColumns={setSelectedColumns}
      label={resolvedLabel}
      title={resolvedTitle}
    />
  )
}

const toTableColumnDef = (column: ReportColumnDefinition): TableColumnDef => ({
  id: column.value,
  label: column.label,
  description: column.description,
  group: column.group,
  toggleable: column.value !== 'groupBy',
  width: column.width,
})
